// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import Channels from '@app/features/channel/state/Channels';
import Navigation from '@app/features/navigation/state/Navigation';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import PopoutWindowManager from '@app/features/voice/state/PopoutWindowManager';
import VoiceCallFullscreen from '@app/features/voice/state/VoiceCallFullscreen';
import {roomViewFor} from '@app/features/voice_scene/state/RoomView';
import VoiceRoomControlDiscovery from '@app/features/voice_scene/state/VoiceRoomControlDiscovery';
import VoiceSceneSettings from '@app/features/voice_scene/state/VoiceSceneSettings';
import VoiceSceneTvRequest from '@app/features/voice_scene/state/VoiceSceneTvRequest';
import type {RoomViewReason} from '@app/features/voice_scene/utils/RoomViewAvailability';
import type {MessageDescriptor} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useCallback} from 'react';

const OPEN_ROOM_DESCRIPTOR = msg({
	message: '3D room',
	comment: 'Voice panel and call control bar button: open the connected channel in its 3D room view.',
});
const CLOSE_ROOM_DESCRIPTOR = msg({
	message: 'Close 3D room',
	comment: 'Voice panel and call control bar button: return to the flat call view while keeping the call expanded.',
});
const ROOM_CONTROL_REASONS: ReadonlyArray<RoomViewReason> = ['room', 'user_flat', 'context_lost', 'load_failed'];

export interface VoiceRoomControl {
	show: boolean;
	displayed: boolean;
	label: MessageDescriptor;
	toggle: () => void;
}

// The 3D room switch of the connected call; read from an observer so it follows the call, channel and room state.
export function useVoiceRoomControl(): VoiceRoomControl {
	const room = MediaEngine.room;
	const isConnected = Boolean(room?.localParticipant || (!room && MediaEngine.connected));
	const channelId = MediaEngine.channelId;
	const channel = channelId ? Channels.getChannel(channelId) : null;
	const roomView = channel ? roomViewFor(channel) : null;
	const show =
		isConnected &&
		!MobileLayout.isMobileLayout() &&
		roomView !== null &&
		ROOM_CONTROL_REASONS.includes(roomView.reason);
	const scopeKey = channel ? `${channel.guildId ? 'guild-voice' : 'dm-call'}:${channel.id}` : null;
	const displayed = Boolean(
		show &&
			channel &&
			scopeKey &&
			Navigation.channelId === channel.id &&
			VoiceCallFullscreen.isScopeActive(scopeKey) &&
			!PopoutWindowManager.isCallPopoutOpenForChannel(channel.id) &&
			!VoiceSceneTvRequest.fullscreen &&
			roomView?.show,
	);
	const toggle = useCallback(() => {
		if (!show || !channel || !scopeKey) return;
		VoiceRoomControlDiscovery.dismiss();
		if (displayed) {
			VoiceSceneSettings.setPreferRoom(false);
			return;
		}
		if (VoiceSceneSettings.failure) VoiceSceneSettings.retry();
		else VoiceSceneSettings.setPreferRoom(true);
		RouterUtils.transitionTo(
			channel.guildId ? Routes.guildChannel(channel.guildId, channel.id) : Routes.dmChannel(channel.id),
		);
		VoiceCallFullscreen.open(scopeKey);
	}, [channel, displayed, scopeKey, show]);
	return {show, displayed, label: displayed ? CLOSE_ROOM_DESCRIPTOR : OPEN_ROOM_DESCRIPTOR, toggle};
}
